import React, { useCallback, useEffect, useState } from 'react';
import { IdpConfigWizard } from './IdpConfigWizard';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import * as api from '../../services/api-client';
import { useAuth } from '../../state/auth-context';

export interface IdpManagementPanelProps {
  readonly onUpdate: () => void;
}

interface IdpConfigData {
  readonly type: 'oidc' | 'saml';
  readonly metadataUrl: string;
  readonly clientId: string;
  readonly adminGroupId?: string;
  readonly attributeMappings: Record<string, string>;
  readonly providerName?: string;
  readonly configuredAt?: string;
}

/**
 * Admin panel for viewing and managing configured Identity Providers.
 * Shows the current IdP configuration and allows editing or adding new providers.
 *
 * Cognito supports multiple IdPs — this panel manages the primary "CoreFlowIdP"
 * and could be extended to support additional providers.
 */
export function IdpManagementPanel({ onUpdate }: IdpManagementPanelProps): React.ReactElement {
  const [idpConfig, setIdpConfig] = useState<IdpConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchIdpConfig = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ data: IdpConfigData | null }>('/v1/admin/idp');
      setIdpConfig(response.data);
    } catch (err: unknown) {
      if (api.isClientError(err)) {
        setError(err.message);
      } else {
        setError('Failed to load IdP configuration');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchIdpConfig();
    }
  }, [fetchIdpConfig, authLoading, isAuthenticated]);

  const handleEditComplete = useCallback((): void => {
    setEditMode(false);
    fetchIdpConfig();
    onUpdate();
  }, [fetchIdpConfig, onUpdate]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4" role="status" aria-label="Loading IdP configuration">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
        <span className="text-sm text-muted-foreground">Loading IdP configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="outline" size="sm" onClick={fetchIdpConfig}>
          Retry
        </Button>
      </div>
    );
  }

  // If in edit mode, show the wizard for updating configuration
  if (editMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Update your Identity Provider configuration below.</p>
          <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
            Cancel
          </Button>
        </div>
        <IdpConfigWizard onComplete={handleEditComplete} />
      </div>
    );
  }

  // No IdP configured yet (shouldn't normally happen in configured mode, but handle gracefully)
  if (!idpConfig) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          No Identity Provider is currently configured.
        </p>
        <Button onClick={() => setEditMode(true)}>
          Configure Identity Provider
        </Button>
      </div>
    );
  }

  // Display current IdP configuration
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">Current Configuration</h3>
        <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
          Edit Configuration
        </Button>
      </div>

      <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <div className="space-y-1">
          <dt className="font-medium text-muted-foreground">Protocol</dt>
          <dd className="text-foreground">{idpConfig.type.toUpperCase()}</dd>
        </div>

        <div className="space-y-1">
          <dt className="font-medium text-muted-foreground">Client ID</dt>
          <dd className="text-foreground font-mono text-xs break-all">{idpConfig.clientId}</dd>
        </div>

        <div className="space-y-1 sm:col-span-2">
          <dt className="font-medium text-muted-foreground">Discovery URL</dt>
          <dd className="text-foreground font-mono text-xs break-all">{idpConfig.metadataUrl}</dd>
        </div>

        {idpConfig.adminGroupId && (
          <div className="space-y-1">
            <dt className="font-medium text-muted-foreground">Admin Group</dt>
            <dd className="text-foreground">{idpConfig.adminGroupId}</dd>
          </div>
        )}

        {idpConfig.configuredAt && (
          <div className="space-y-1">
            <dt className="font-medium text-muted-foreground">Last Configured</dt>
            <dd className="text-foreground">{new Date(idpConfig.configuredAt).toLocaleDateString()}</dd>
          </div>
        )}
      </dl>

      {Object.keys(idpConfig.attributeMappings).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Attribute Mappings</h4>
          <div className="rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left font-medium">IdP Claim</th>
                  <th className="px-3 py-2 text-left font-medium">CoreFlow Property</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(idpConfig.attributeMappings).map(([source, target]) => (
                  <tr key={source} className="border-b last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{source}</td>
                    <td className="px-3 py-2">{target}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Alert>
        <AlertDescription>
          Cognito supports multiple identity providers. To add an additional IdP,
          you can reconfigure or extend the federation setup above. Changes take
          effect immediately for new login sessions.
        </AlertDescription>
      </Alert>
    </div>
  );
}
